import { Component, Element, h, Host, Prop, State, Watch } from '@stencil/core';
import { ChartConfiguration } from 'chart.js';

import { LineChartGridOptions, LineChartMarkerPointOptions } from '../../../services/interfaces';
import { ChartJsService } from '../../../services/libraries';
import { getCSSVariableValue, parsePropToArray } from '../../../services/utils';

let mixedChartId = 0;

/**
 * @part base - component wrapper container inside the Shadow DOM
 * @part canvas - wrapper container that renders the Chart.js canvas
 * @part canvas-outline - wrapper container around the canvas
 */
@Component({
  tag: 'pk-mixed-chart',
  styleUrl: './scss/pk-mixed-chart.scss',
  shadow: true,
})
export class PkMixedChart {
  // Own Properties
  // ====================

  private chart: ChartJsService;
  private canvasElemRef: HTMLCanvasElement;
  private hostShadowRoot: Element;
  private mixedChartId = `mixed-chart-${++mixedChartId}`;

  // Reference to host HTML element
  // ===================================

  @Element() el: HTMLPkLineChartElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  @State() chartConfig: ChartConfiguration;
  @State() chartDataLine: number[];
  @State() chartDataColumnBottom: number[];
  @State() chartDataColumnTop: number[];
  @State() chartLabels: string[];

  // Public Property API
  // ========================

  /** Specify the aria label text for accessibility */
  @Prop({ reflect: true }) ariaLabel: string | null = 'Panenka mixed chart';

  /** It is applied to the width of each column bar */
  @Prop({ reflect: true }) barThickness: number = 12;

  /** It is applied to all corners of each column bar */
  @Prop({ reflect: true }) barBorderRadius: number = 10;

  /** If true, the tooltip over the line and bar chart data won't be shown */
  @Prop({ reflect: true }) disableTooltip: boolean = false;

  /** The series values for the bar column chart stacked at the bottom. The series property expects a single array of numbers */
  @Prop({ reflect: true }) dataColumnBottom: string | number[];

  /** Text label for the bottom bar chart  */
  @Prop({ reflect: true }) labelColumnBottom: string;

  /** The series values for the bar column chart stacked at the top. The series property expects a single array of numbers */
  @Prop({ reflect: true }) dataColumnTop: string | number[];

  /** Text label for the top bar chart  */
  @Prop({ reflect: true }) labelColumnTop: string;

  /** The series values for the line chart. The series property expects a single array of numbers */
  @Prop({ reflect: true }) dataLine: string | number[];

  /** Text label for the line chart  */
  @Prop({ reflect: true }) labelLine: string;

  /** The width of the border line */
  @Prop({ reflect: true }) gridBorderWidth: number = 0;

  /** Stroke width of grid lines */
  @Prop({ reflect: true }) gridLineWidth: number = 2;

  /** Width of the tick mark in pixels */
  @Prop({ reflect: true }) gridTickWidth: number = 0;

  /** It will set the height of the SVG chart (only the circle) */
  @Prop({ reflect: true }) height: number = 250;

  /** Labels for the X axis of the chart. The labels property expects a single array */
  @Prop({ reflect: true }) labels: string | string[];

  /** Line stroke width */
  @Prop({ reflect: true }) lineStroke: number = 3;

  /** It sets how the lines grid are displayed (`default`: only `horizontal` grid lines) */
  @Prop({ reflect: true }) lineGrid: LineChartGridOptions = 'default';

  /** Whether to display or not the marker points in the line of the chart */
  @Prop({ reflect: true }) markerPoint: LineChartMarkerPointOptions = 'default';

  // Prop lifecycle events
  // =======================

  @Watch('dataLine')
  updateLineChartData() {
    this.chartDataLine = parsePropToArray(this.dataLine);
    this.chartConfig.data.datasets[0].data = this.chartDataLine;
  }

  @Watch('dataColumnBottom')
  updateBottomColumnChartData() {
    this.chartDataColumnBottom = parsePropToArray(this.dataColumnBottom);
    this.chartConfig.data.datasets[1].data = this.chartDataColumnBottom;
  }

  @Watch('dataColumnTop')
  updateTopColumnChartData() {
    this.chartDataColumnTop = parsePropToArray(this.dataColumnTop);
    this.chartConfig.data.datasets[2].data = this.chartDataColumnTop;
  }

  @Watch('labels')
  updateLabels() {
    this.chartLabels = parsePropToArray(this.labels);
    this.chartConfig.data.labels = this.chartLabels;
  }

  @Watch('dataLine')
  @Watch('labelLine')
  @Watch('dataColumnBottom')
  @Watch('labelColumnBottom')
  @Watch('dataColumnTop')
  @Watch('labelColumnTop')
  @Watch('labels')
  onDataLabelChange() {
    if (!this.chart) return;

    this.chart.update(this.chartConfig);
  }

  @Watch('barThickness')
  @Watch('barBorderRadius')
  @Watch('disableTooltip')
  @Watch('gridBorderWidth')
  @Watch('gridLineWidth')
  @Watch('gridTickWidth')
  @Watch('height')
  @Watch('lineGrid')
  @Watch('lineStroke')
  @Watch('markerPoint')
  onConfigChange() {
    if (!this.chart) return;

    this.chart.destroy();
    this.initChart();
  }

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  componentWillLoad() {
    this.chartDataLine = parsePropToArray(this.dataLine);
    this.chartDataColumnBottom = parsePropToArray(this.dataColumnBottom);
    this.chartDataColumnTop = parsePropToArray(this.dataColumnTop);
    this.chartLabels = parsePropToArray(this.labels);
  }

  componentDidLoad() {
    this.hostShadowRoot = this.el.shadowRoot.host;
    this.initChart();
  }

  // Listeners
  // ==============

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  private setChartConfig = () => {
    if (!this.hostShadowRoot) return;

    this.chartConfig = {
      type: 'bar',
      data: {
        labels: this.chartLabels,
        datasets: [
          // Line chart
          {
            type: 'line',
            fill: false,
            data: this.chartDataLine,
            label: this.labelLine,
            yAxisID: 'line',
            borderColor: this.cssValue('mixed-chart-line--background-color'),
            backgroundColor: this.cssValue('mixed-chart-line--background-color'),
            ...this.datasetLineConfig(),
          },
          // Bottom Column chart
          {
            type: 'bar',
            data: this.chartDataColumnBottom,
            label: this.labelColumnBottom,
            yAxisID: 'column',
            backgroundColor: this.cssValue('mixed-chart-bar-bottom--background-color'),
            hoverBackgroundColor: this.cssValue('mixed-chart-bar-bottom--background-color'),
            barThickness: this.barThickness,
            borderRadius: this.barBorderRadius,
          },
          // Top Column chart
          {
            type: 'bar',
            data: this.chartDataColumnTop,
            label: this.labelColumnTop,
            yAxisID: 'column',
            backgroundColor: this.cssValue('mixed-chart-bar-top--background-color'),
            hoverBackgroundColor: this.cssValue('mixed-chart-bar-top--background-color'),
            barThickness: this.barThickness,
            borderRadius: this.barBorderRadius,
          },
        ],
      },
      options: {
        animation: {
          easing: 'easeInOutBack',
          duration: 2000,
        },
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            top: 40,
          },
        },
        plugins: {
          tooltip: {
            enabled: !this.disableTooltip,
            // Element Border radius
            cornerRadius: parseInt(getCSSVariableValue('pk-border-radius-l', document.body), 10),
            // Element padding
            padding: 12,
            // Title content
            titleColor: getCSSVariableValue('pk-text-04', document.body),
            titleFont: {
              family: getCSSVariableValue('pk-font-base-semibold', document.body),
              size: parseInt(getCSSVariableValue('pk-font-size-s', document.body), 10),
            },
            titleMarginBottom: 4,
            // Body content
            bodyColor: getCSSVariableValue('pk-text-04', document.body),
            bodyFont: {
              family: getCSSVariableValue('pk-font-base-regular', document.body),
              size: parseInt(getCSSVariableValue('pk-font-size-xs', document.body), 10),
            },
            // Square element inside the body content
            boxHeight: 12,
            boxWidth: 12,
            boxPadding: 4,
          },
        },
        scales: {
          x: {
            grid: {
              display: this.lineGrid === LineChartGridOptions[2],
              color: this.cssValue('mixed-chart-tick--border-color'),
            },
            border: {
              dash: [2, 4],
              width: this.gridBorderWidth,
            },
            stacked: true,
          },
          column: {
            position: 'right',
            grid: {
              display: false,
            },
            stacked: true,
            border: {
              width: this.gridBorderWidth,
            },
            ticks: {
              ...this.scalesTicksConfig(),
            },
          },
          line: {
            position: 'left',
            grid: {
              display: this.lineGrid !== LineChartGridOptions[3],
              color: this.cssValue('mixed-chart-tick--border-color'),
            },
            border: {
              dash: [2, 4],
              width: this.gridBorderWidth,
            },
            ticks: {
              ...this.scalesTicksConfig(),
            },
          },
        },
      },
    };
  };

  private initChart = () => {
    this.setChartConfig();
    this.chart = new ChartJsService(this.canvasElemRef, this.chartConfig);
  };

  private datasetLineConfig = () => {
    if (!this.canvasElemRef) return;

    return {
      // Datasets configuration options:
      // https://www.chartjs.org/docs/latest/charts/line.html#dataset-properties
      borderWidth: this.lineStroke,
      pointStyle: 'circle',
      pointBackgroundColor: this.cssValue('mixed-chart-point--background-color'),
      pointBorderColor: this.cssValue('mixed-chart-point--border-color'),
      pointBorderWidth: this.cssValue2Int('mixed-chart-point--border-width'),
      pointHoverBackgroundColor: this.cssValue('mixed-chart-point--hover-background-color'),
      pointHoverBorderColor: this.cssValue('mixed-chart-point--hover-border-color'),
      pointHoverBorderWidth: this.cssValue2Int('mixed-chart-point--hover-border-width'),
      pointRadius:
        this.markerPoint === LineChartMarkerPointOptions[1] ? 0 : this.cssValue2Int('mixed-chart-point--border-radius'),
      pointHoverRadius:
        this.markerPoint === LineChartMarkerPointOptions[1]
          ? 0
          : this.cssValue2Int('mixed-chart-point--hover-border-radius'),
    };
  };

  private scalesTicksConfig = () => ({
    // Tick configuration options
    // https://www.chartjs.org/docs/latest/axes/styling.html#tick-configuration
    color: this.cssValue('mixed-chart-tick--color'),
    display: true,
    font: { size: this.cssValue2Int('mixed-chart-tick--font-size') },
    padding: this.cssValue2Int('mixed-chart-tick--padding'),
    tickBorderDash: [2, 4],
  });

  private cssValue = (cssVar: string) => getCSSVariableValue(cssVar, this.hostShadowRoot);

  private cssValue2Int = (cssVar: string) => parseInt(getCSSVariableValue(cssVar, this.hostShadowRoot), 10);

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    const style = {
      ...(this.height && { '--mixed-chart--height': `${this.height}px` }),
    };

    return (
      <Host style={style} role="img">
        <div class="block h-[--mixed-chart--height] w-full leading-none" part="base">
          <div class="relative h-[--mixed-chart--height] w-full" part="canvas-outline">
            <canvas
              aria-label={this.ariaLabel}
              id={this.mixedChartId}
              ref={(canvas: HTMLCanvasElement) => (this.canvasElemRef = canvas)}
              part="canvas"
              role="img"
            >
              <p class="text-center font-regular text-m text-text-03">
                Your browser does not support the canvas element.
              </p>
            </canvas>
          </div>
        </div>
      </Host>
    );
  }
}
